import React from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { Column, ColumnEditorOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DatasetChartProps, DatasetSpecification } from "./interfaces";

interface IBarChartIdentifiersEditorProps {
  currentConfig: DatasetChartProps;
  setCurrentConfig: React.Dispatch<React.SetStateAction<DatasetChartProps>>;
}

const BarsIdentifiersEditor = ({ currentConfig, setCurrentConfig }: IBarChartIdentifiersEditorProps) => {
  const colorEditor = (options: ColumnEditorOptions) => (
    <ColorPicker value={options.value} onValueChange={e => options.editorCallback?.(e.target.value)} />
  );

  const colorBodyTemplate = (rowData: DatasetSpecification, field: string) => {
    return (
      <div className="flex justify-content-center">
        {field === "color" ? (
          <div
            style={{
              backgroundColor: rowData.color,
              width: "25px",
              height: "25px",
              border: "1px solid black",
            }}
          />
        ) : (
          <div
            style={{
              backgroundColor: rowData.bgColor,
              width: "25px",
              height: "25px",
              border: "1px solid black",
            }}
          />
        )}
      </div>
    );
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData, index } = e;
    const datasetsToUpdate = [...currentConfig.datasets!];

    datasetsToUpdate[index] = { ...datasetsToUpdate[index], ...newData };

    setCurrentConfig({
      ...currentConfig,
      datasets: datasetsToUpdate,
    });
  };

  const titleBodyTemplate = (rowData: DatasetSpecification) => {
    return <DatasetTitle identifier={rowData.identifier} />;
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={e => options.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  const overriddenTitleBodyTemplate = (rowData: DatasetSpecification) => {
    return <div>{rowData.overriddenTitle}</div>;
  };

  const identifierEditor = (options: ColumnEditorOptions) => {
    return <DatasetDropdown value={options.value || ""} onChange={e => options.editorCallback?.(e)} />;
  };

  return (
    <DataTable value={currentConfig.datasets} editMode="row" onRowEditComplete={onRowEditComplete}>
      <Column
        field="identifier"
        header={T("word.identifier")}
        body={titleBodyTemplate}
        editor={identifierEditor}
      />
      <Column
        field="overriddenTitle"
        header={T("word.overridden-title")}
        editor={textEditor}
        body={overriddenTitleBodyTemplate}
      />
      <Column
        className="p-0 w-9rem"
        field="color"
        header={T("word.color")}
        align="center"
        editor={colorEditor}
        body={rowData => colorBodyTemplate(rowData, "color")}
      />
      <Column rowEditor />
    </DataTable>
  );
};

export default BarsIdentifiersEditor;
